/**
 * Casos Prioritarios — el módulo de decisión central, cableado al
 * **Motor_Casos** de la Capa Operacional AI-First (tarea 2.12).
 *
 * La vista consume el servicio `MotorCasos` resuelto por el runtime
 * selector (`../api/runtime`): la evidencia se deriva de los JSONs
 * reales de Antofagasta (`dmas.json`, `alarmas.json`) y el motor produce
 * los Casos con su Score_Caso, Explainability con las nueve
 * contribuciones con signo, Cadena_Causal, timeline de Eventos y
 * acciones sugeridas vinculadas al Registro. Toda la lógica de scoring,
 * orden y explicabilidad vive en el motor; aquí solo se compone la UI.
 *
 * Layout maestro/detalle: ranking ordenado por Score_Caso a la izquierda
 * (R3.4) y ficha de decisión a la derecha con el desglose de las
 * contribuciones (R4.1), la Cadena_Causal (R4.4), el timeline (R4.5) y
 * las acciones sugeridas (R4.7). El recálculo es **reactivo** ante nueva
 * evidencia (R3.7): inyectar evidencia re-puntúa y reordena en vivo.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import type {
  AccionSugerida,
  Caso,
  EntidadRef,
  EventoOperacional,
  Evidencia,
  MotorCasos,
} from "../api/contracts";
import { runtime } from "../api/runtime";
import { loadRealData } from "../api/realData";
import { ensureDemoProviders } from "../lib/demoRuntime";
import { derivarEvidencias } from "../lib/motorCasos";
import {
  type CasoEnriquecido,
  graphDataDeReal,
  ingestarYEnriquecer,
  paramsDeAccionCaso,
  timelineDeCaso,
} from "../lib/casosRuntime";
import { Icon } from "../components/Icon";
import { PageHeader } from "../layout/PageHeader";
import { useAuth } from "../auth/AuthContext";
import { useCopilot } from "../system/CopilotContext";
import { useToast } from "../system/ToastContext";
import { exportRowsToCsv, exportTextPdf } from "../lib/exporters";

const BANDAS = [
  { key: "todas", label: "Todas" },
  { key: "P1", label: "P1" },
  { key: "P2", label: "P2" },
  { key: "P3", label: "P3" },
  { key: "P4", label: "P4" },
];

/** Metadatos de DMA para enriquecer la ficha (nombre, clientes, NRW). */
interface DmaMeta {
  codigo: string;
  nombre: string;
  clientes: number;
  nrw_pct: number;
  fugas: number;
}

function bandClass(b: string): "critica" | "alta" | "media" | "baja" {
  if (b === "P1") return "critica";
  if (b === "P2") return "alta";
  if (b === "P3") return "media";
  return "baja";
}

function bandTone(b: string): "danger" | "warn" | "info" | "neutral" {
  if (b === "P1") return "danger";
  if (b === "P2") return "warn";
  if (b === "P3") return "info";
  return "neutral";
}

/** Etiqueta legible del tipo de Anomalia derivada del `evidencia_ref.id`. */
function etiquetaAnomalia(ref: EntidadRef): string {
  const idx = ref.id.lastIndexOf(":");
  const etq = idx >= 0 ? ref.id.slice(idx + 1) : ref.id;
  return etq.replace(/_/g, " ");
}

/** Título humano de un Caso a partir de su evidencia y entidad raíz. */
function tituloDeCaso(caso: Caso, dma?: DmaMeta): string {
  const etiquetas = Array.from(
    new Set(
      caso.evidencia
        .filter((e) => e.evidencia_ref.tipo === "Anomalia")
        .map((e) => etiquetaAnomalia(e.evidencia_ref)),
    ),
  );
  const dominante = etiquetas[0] ? etiquetas[0][0].toUpperCase() + etiquetas[0].slice(1) : "Evidencia correlacionada";
  const zona = dma?.nombre ? `${caso.entidad_raiz_ref.id} · ${dma.nombre}` : caso.entidad_raiz_ref.id;
  return `${dominante} · ${zona}`;
}

interface CasosState {
  loading: boolean;
  error: string | null;
  casos: CasoEnriquecido[];
  dmas: Map<string, DmaMeta>;
}

/**
 * Hook de cableado al Motor_Casos. Registra los proveedores demo, carga
 * los datos reales, deriva la evidencia y la ingesta en el motor,
 * devolviendo los Casos enriquecidos y ordenados por Score_Caso. Expone
 * `inyectarEvidencia` para el recálculo reactivo (R3.7).
 */
function usePriorityCases() {
  const [state, setState] = useState<CasosState>({
    loading: true,
    error: null,
    casos: [],
    dmas: new Map(),
  });
  const motorRef = useRef<MotorCasos | null>(null);
  const evidenciasRef = useRef<Evidencia[]>([]);
  const contadorRef = useRef(0);
  const [ultimoRecalculoMs, setUltimoRecalculoMs] = useState<number | null>(null);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      ensureDemoProviders();
      const motor = runtime.motorCasos();
      motorRef.current = motor;

      const real = await loadRealData();
      const dmaMeta = new Map<string, DmaMeta>();
      for (const d of real?.dmas ?? []) {
        dmaMeta.set(d.codigo, {
          codigo: d.codigo,
          nombre: d.nombre,
          clientes: d.clientes,
          nrw_pct: d.nrw_pct,
          fugas: d.fugas,
        });
      }

      const graphData = graphDataDeReal(real);
      const evidencias = graphData ? derivarEvidencias(graphData) : [];
      evidenciasRef.current = evidencias;

      const casos = await ingestarYEnriquecer(motor, evidencias);
      if (cancelado) return;
      setState({ loading: false, error: null, casos, dmas: dmaMeta });
    })().catch((e) => {
      if (!cancelado) {
        setState((s) => ({ ...s, loading: false, error: String(e) }));
      }
    });
    return () => {
      cancelado = true;
    };
  }, []);

  /**
   * Inyecta una nueva pieza de evidencia (Anomalia) sobre la entidad raíz
   * indicada y re-puntúa/reordena los Casos. Mide la latencia del
   * recálculo para evidenciar el cumplimiento del umbral de 2s (R3.7).
   */
  const inyectarEvidencia = useCallback(
    async (raiz: EntidadRef, etiqueta = "fuga"): Promise<number> => {
      const motor = motorRef.current;
      if (!motor) return 0;
      const t0 =
        typeof performance !== "undefined" && typeof performance.now === "function"
          ? performance.now()
          : Date.now();

      contadorRef.current += 1;
      const anomRef: EntidadRef = {
        tipo: "Anomalia",
        id: `inj-${contadorRef.current}:${etiqueta}`,
      };
      const nueva: Evidencia = {
        evidencia_ref: anomRef,
        entidades: [anomRef, raiz],
        ts: new Date().toISOString(),
      };
      evidenciasRef.current = [...evidenciasRef.current, nueva];

      const casos = await ingestarYEnriquecer(motor, evidenciasRef.current);
      const t1 =
        typeof performance !== "undefined" && typeof performance.now === "function"
          ? performance.now()
          : Date.now();
      const dt = Math.max(0, Math.round(t1 - t0));
      setUltimoRecalculoMs(dt);
      setState((s) => ({ ...s, casos }));
      return dt;
    },
    [],
  );

  return { ...state, inyectarEvidencia, ultimoRecalculoMs };
}

export function PriorityCases(): JSX.Element {
  const [banda, setBanda] = useState("todas");
  const [activeId, setActiveId] = useState<string | null>(null);
  const { toast } = useToast();
  const { loading, error, casos, dmas, inyectarEvidencia, ultimoRecalculoMs } =
    usePriorityCases();

  const visibles = useMemo(
    () => (banda === "todas" ? casos : casos.filter((c) => c.banda === banda)),
    [casos, banda],
  );

  const active = useMemo(
    () => visibles.find((c) => c.caso.caso_id === activeId) ?? visibles[0],
    [visibles, activeId],
  );

  const totals = useMemo(() => {
    const p1 = casos.filter((c) => c.banda === "P1").length;
    const parciales = casos.filter((c) => c.caso.score.parcial).length;
    const evidencia = casos.reduce((acc, c) => acc + c.caso.evidencia.length, 0);
    const promedio =
      casos.length > 0
        ? Math.round(casos.reduce((acc, c) => acc + (c.caso.score.valor ?? 0), 0) / casos.length)
        : 0;
    return { p1, parciales, evidencia, promedio };
  }, [casos]);

  return (
    <>
      <PageHeader
        eyebrow="Centro de mando"
        title="Casos Prioritarios"
        subtitle="Motor_Casos: evidencia correlacionada de Antofagasta, priorizada por Score_Caso compuesto y explicable."
        actions={
          <>
            <button
              className="btn"
              onClick={() => {
                const ok = exportRowsToCsv(
                  `casos-prioritarios-${new Date().toISOString().slice(0, 10)}.csv`,
                  casos.map((c) => ({
                    caso_id: c.caso.caso_id,
                    score: c.caso.score.valor,
                    banda: c.banda,
                    parcial: c.caso.score.parcial,
                    entidad_raiz: c.caso.entidad_raiz_ref.id,
                    evidencia: c.caso.evidencia.length,
                    acciones: c.acciones.length,
                    estado: c.caso.estado,
                  })),
                );
                toast(
                  ok ? `Exportados ${casos.length} casos a CSV` : "No hay casos para exportar",
                  ok ? "success" : "warn",
                );
              }}
            >
              <Icon name="report" size={13} /> Exportar ranking
            </button>
            <button
              className="btn btn--primary"
              disabled={!active}
              onClick={async () => {
                if (!active) return;
                const dt = await inyectarEvidencia(active.caso.entidad_raiz_ref);
                toast(
                  `Nueva evidencia ingerida en ${active.caso.entidad_raiz_ref.id} · recálculo en ${dt} ms`,
                  dt <= 2000 ? "success" : "warn",
                );
              }}
            >
              <Icon name="flask" size={13} /> Inyectar evidencia
            </button>
          </>
        }
      />

      <div className="card-grid" style={{ marginBottom: 16 }}>
        <div className="kpi kpi--danger">
          <span className="kpi__label">Casos P1</span>
          <span className="kpi__value">{totals.p1}</span>
          <span className="kpi__hint">Score_Caso ≥ 80</span>
        </div>
        <div className="kpi kpi--accent">
          <span className="kpi__label">Score promedio</span>
          <span className="kpi__value">{totals.promedio}</span>
          <span className="kpi__hint">de {casos.length} casos abiertos</span>
        </div>
        <div className="kpi kpi--info">
          <span className="kpi__label">Evidencia correlacionada</span>
          <span className="kpi__value">{totals.evidencia.toLocaleString("es-CL")}</span>
          <span className="kpi__hint">piezas agrupadas en casos</span>
        </div>
        <div className="kpi">
          <span className="kpi__label">Cálculos parciales</span>
          <span className="kpi__value">{totals.parciales}</span>
          <span className="kpi__hint">componentes sin datos (R3.8)</span>
        </div>
      </div>

      <div className="hstack" style={{ marginBottom: 12 }}>
        <div className="segmented">
          {BANDAS.map((p) => (
            <button
              key={p.key}
              type="button"
              className="segmented__btn"
              aria-pressed={banda === p.key}
              onClick={() => setBanda(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <span className="flex-spacer" />
        {ultimoRecalculoMs !== null && (
          <span className="text-muted" style={{ fontSize: 12 }}>
            Último recálculo: {ultimoRecalculoMs} ms ·{" "}
          </span>
        )}
        <span className="text-muted" style={{ fontSize: 12 }}>
          Ordenado por Score_Caso ↓
        </span>
      </div>

      <div className="alert-shell">
        <div className="alert-list">
          {loading && (
            <div className="status-block status-block--info">Correlacionando evidencia…</div>
          )}
          {error && (
            <div className="status-block status-block--danger">
              No se pudo cargar el Motor_Casos: {error}
            </div>
          )}
          {!loading && !error && visibles.length === 0 && (
            <div className="status-block">No hay casos para la banda seleccionada.</div>
          )}
          {visibles.map((c) => {
            const score = Math.round(c.caso.score.valor ?? 0);
            const dma = dmas.get(c.caso.entidad_raiz_ref.id);
            return (
              <div
                key={c.caso.caso_id}
                className="alert-row"
                data-active={(active?.caso.caso_id ?? "") === c.caso.caso_id}
                onClick={() => setActiveId(c.caso.caso_id)}
                style={{ gridTemplateColumns: "64px 1fr auto" }}
              >
                <div className={`alert-score alert-score--${bandClass(c.banda)}`}>{score}</div>
                <div>
                  <div className="alert-row__title">{tituloDeCaso(c.caso, dma)}</div>
                  <div className="alert-row__meta">
                    <span className={`sev-pill sev-pill--${bandClass(c.banda)}`}>{c.banda}</span>
                    <span className="text-mono">{c.caso.evidencia.length} evidencia(s)</span>
                    <span className="text-muted">·</span>
                    <span>{c.acciones.length} acción(es)</span>
                    {c.caso.score.parcial && (
                      <>
                        <span className="text-muted">·</span>
                        <span className="badge badge--warn">parcial</span>
                      </>
                    )}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <div className="sla-bar">
                      <div
                        className={`sla-bar__fill sla-bar__fill--${
                          score >= 80 ? "late" : score >= 65 ? "warn" : "ok"
                        }`}
                        style={{ width: `${Math.min(100, score)}%` }}
                      />
                    </div>
                  </div>
                </div>
                <div className="alert-row__sla">
                  <div>{c.caso.entidad_raiz_ref.tipo}</div>
                  <div className="text-muted" style={{ marginTop: 2 }}>
                    {c.caso.estado}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {active && <CaseDetail item={active} dma={dmas.get(active.caso.entidad_raiz_ref.id)} />}
      </div>
    </>
  );
}

function CaseDetail({ item, dma }: { item: CasoEnriquecido; dma?: DmaMeta }): JSX.Element {
  const { openCopilot } = useCopilot();
  const { toast } = useToast();
  const { activeRole } = useAuth();
  const { caso, contribuciones, acciones, cadena, banda } = item;
  const score = Math.round(caso.score.valor ?? 0);

  /**
   * Resultado de cada acción propuesta vía el Registro, indexado por
   * `accion_semantica_id`. Refleja en la UI el desenlace del gate HITL/
   * policy: ejecutada, pendiente de aprobación humana (crítica), denegada
   * por permisos/política o un error degradado.
   */
  type EstadoAccion =
    | { fase: "invocando" }
    | { fase: "ejecutada"; detalle?: string }
    | { fase: "pendiente"; solicitud_id?: string; detalle?: string }
    | { fase: "denegada"; detalle: string };
  const [estadoAcciones, setEstadoAcciones] = useState<Record<string, EstadoAccion>>({});

  /**
   * Propone una acción sugerida **siempre a través del Registro**
   * (`runtime.registry().invoke`, R4.7, R6.6): la UI nunca ejecuta
   * directamente. El actor es el rol activo de la sesión (useAuth); los
   * params se derivan de la entidad raíz del Caso. El Registro aplica el
   * gate de permisos/políticas y mantiene en PENDIENTE las críticas, que
   * solo un humano con permiso puede aprobar. El resultado se refleja en
   * la ficha.
   */
  const proponerAccion = useCallback(
    async (a: AccionSugerida) => {
      const id = a.accion_semantica_id;
      const actor = activeRole ?? "anonimo";
      const params = paramsDeAccionCaso(caso);
      setEstadoAcciones((s) => ({ ...s, [id]: { fase: "invocando" } }));

      const res = await runtime.registry().invoke(id, params, actor);

      if (!res.ok) {
        setEstadoAcciones((s) => ({ ...s, [id]: { fase: "denegada", detalle: res.error } }));
        toast(`Acción «${id}» denegada: ${res.error}`, "danger");
        return;
      }

      const r = res.value;
      if (r.estado === "pendiente") {
        setEstadoAcciones((s) => ({
          ...s,
          [id]: { fase: "pendiente", solicitud_id: r.solicitud_id, detalle: r.detalle },
        }));
        toast(
          `Acción crítica «${id}» en estado pendiente — requiere aprobación humana`,
          "warn",
        );
        return;
      }

      if (r.estado === "denegada") {
        setEstadoAcciones((s) => ({
          ...s,
          [id]: { fase: "denegada", detalle: r.detalle ?? "Acción denegada por el Registro." },
        }));
        toast(`Acción «${id}» denegada`, "danger");
        return;
      }

      setEstadoAcciones((s) => ({ ...s, [id]: { fase: "ejecutada", detalle: r.detalle } }));
      toast(`Acción «${id}» ejecutada para ${caso.caso_id}`, "success");
    },
    [activeRole, caso, toast],
  );

  const [timeline, setTimeline] = useState<EventoOperacional[]>([]);
  useEffect(() => {
    let cancelado = false;
    const motor = runtime.motorCasos();
    timelineDeCaso(motor, caso).then((evs) => {
      if (!cancelado) setTimeline(evs);
    });
    return () => {
      cancelado = true;
    };
  }, [caso]);

  // Contribuciones disponibles para la barra de composición (R4.1).
  const maxContrib = Math.max(
    ...contribuciones.map((c) => Math.abs(c.contribucion)),
    1,
  );

  return (
    <aside className="alert-detail">
      <div>
        <span className={`badge badge--${bandTone(banda)}`}>{banda}</span>
        <h3 style={{ marginTop: 6, marginBottom: 4 }}>{tituloDeCaso(caso, dma)}</h3>
        <div className="text-muted" style={{ fontSize: 12 }}>
          {caso.entidad_raiz_ref.tipo} {caso.entidad_raiz_ref.id}
          {dma ? ` · ${dma.nombre} · ${dma.clientes.toLocaleString("es-CL")} clientes` : ""}
        </div>
      </div>

      {/* Score_Caso + estado de cálculo */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div className="kpi" style={{ padding: 12 }}>
          <span className="kpi__label">Score_Caso</span>
          <span
            className={`alert-score alert-score--${bandClass(banda)}`}
            style={{ fontSize: 30, textAlign: "left" }}
          >
            {score}
          </span>
          <span className="kpi__hint">
            {caso.score.parcial ? "cálculo parcial" : "cálculo completo"}
          </span>
        </div>
        <div className="kpi kpi--info" style={{ padding: 12 }}>
          <span className="kpi__label">Evidencia</span>
          <span className="kpi__value" style={{ fontSize: 26 }}>
            {caso.evidencia.length}
          </span>
          <span className="kpi__hint">
            {dma ? `NRW ${dma.nrw_pct}% · ${dma.fugas} fugas` : "piezas correlacionadas"}
          </span>
        </div>
      </div>

      {/* Composición del Score: contribuciones disponibles como barras */}
      <div>
        <div className="card__title">Composición del Score_Caso</div>
        {contribuciones
          .filter((c) => !c.no_disponible)
          .slice()
          .sort((a, b) => Math.abs(b.contribucion) - Math.abs(a.contribucion))
          .map((c) => (
            <div key={c.componente} style={{ marginBottom: 8 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 11.5,
                  marginBottom: 3,
                }}
              >
                <span>{c.etiqueta}</span>
                <span className="text-mono text-muted">
                  {c.contribucion >= 0 ? "+" : ""}
                  {c.contribucion.toFixed(1)}
                </span>
              </div>
              <div className="sla-bar">
                <div
                  className="sla-bar__fill sla-bar__fill--ok"
                  style={{
                    width: `${(Math.abs(c.contribucion) / maxContrib) * 100}%`,
                    background: c.contribucion >= 0 ? "var(--c-primary-300)" : "var(--c-danger)",
                  }}
                />
              </div>
            </div>
          ))}
      </div>

      {/* Explainability: las nueve contribuciones con signo (R4.1) */}
      <div>
        <div className="card__title">Explicabilidad del Score (9 componentes)</div>
        {contribuciones.map((c) => (
          <div
            key={c.componente}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
              padding: "5px 0",
              borderBottom: "1px dashed var(--c-divider)",
            }}
          >
            <div>
              <div style={{ fontSize: 12, color: "var(--c-text)" }}>{c.etiqueta}</div>
              {c.no_disponible && (
                <div className="text-faint" style={{ fontSize: 10.5 }}>
                  dato no disponible
                </div>
              )}
            </div>
            <span
              className="text-mono"
              style={{
                fontWeight: 700,
                fontSize: 13,
                color: c.no_disponible
                  ? "var(--c-text-faint)"
                  : c.contribucion >= 0
                    ? "var(--c-success)"
                    : "var(--c-danger)",
              }}
            >
              {c.no_disponible ? "n/d" : `${c.contribucion >= 0 ? "+" : ""}${c.contribucion.toFixed(1)}`}
            </span>
          </div>
        ))}
      </div>

      {/* Acciones sugeridas vinculadas al Registro (R4.7) */}
      <div>
        <div className="card__title">Acciones sugeridas ({acciones.length})</div>
        {acciones.length === 0 && (
          <div className="status-block" style={{ fontSize: 12 }}>
            Sin acción aplicable del Registro_Acciones_Semanticas para este caso.
          </div>
        )}
        {acciones.map((a) => {
          const estado = estadoAcciones[a.accion_semantica_id];
          const invocando = estado?.fase === "invocando";
          return (
            <div
              key={a.accion_semantica_id}
              className="status-block"
              style={{
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 4,
                background: "var(--c-surface-2)",
                borderColor: "var(--c-border)",
              }}
            >
              <div className="hstack" style={{ justifyContent: "space-between", width: "100%" }}>
                <strong className="text-mono" style={{ fontSize: 12 }}>
                  {a.accion_semantica_id}
                </strong>
                <span className="badge badge--neutral">{a.evidencia.length} evidencia(s)</span>
              </div>
              <span style={{ color: "var(--c-text)", fontSize: 12 }}>{a.razonamiento}</span>
              <button
                className="btn btn--ghost"
                style={{ marginTop: 4 }}
                disabled={invocando}
                onClick={() => void proponerAccion(a)}
              >
                <Icon name="wrench" size={12} />{" "}
                {invocando ? "Proponiendo…" : "Proponer acción"}
              </button>

              {/* Desenlace del gate HITL/policy del Registro (R4.7, R6.6) */}
              {estado?.fase === "pendiente" && (
                <div
                  className="badge badge--warn"
                  style={{ marginTop: 4, whiteSpace: "normal", textAlign: "left" }}
                >
                  <Icon name="shield" size={11} /> Pendiente de aprobación humana
                  {estado.solicitud_id ? ` · solicitud ${estado.solicitud_id}` : ""} — acción
                  crítica, no se ejecuta automáticamente.
                </div>
              )}
              {estado?.fase === "ejecutada" && (
                <div
                  className="badge badge--success"
                  style={{ marginTop: 4, whiteSpace: "normal", textAlign: "left" }}
                >
                  <Icon name="shield" size={11} /> Ejecutada por «{activeRole ?? "anonimo"}»
                  {estado.detalle ? ` · ${estado.detalle}` : ""}
                </div>
              )}
              {estado?.fase === "denegada" && (
                <div
                  className="badge badge--danger"
                  style={{ marginTop: 4, whiteSpace: "normal", textAlign: "left" }}
                >
                  <Icon name="shield" size={11} /> Denegada · {estado.detalle}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Razonamiento IA */}
      <div
        className="status-block"
        style={{
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 4,
          background: "var(--c-surface-2)",
          borderColor: "var(--c-border)",
        }}
      >
        <strong
          className="hstack"
          style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, gap: 6 }}
        >
          <Icon name="flask" size={12} /> Profundizar con Copilot
        </strong>
        <button
          className="btn btn--ghost"
          onClick={() =>
            openCopilot(
              `Explícame el caso ${caso.caso_id} de ${caso.entidad_raiz_ref.id}`,
            )
          }
        >
          <Icon name="flask" size={12} /> Analizar este caso
        </button>
      </div>

      {/* Timeline de Eventos_Operacionales (R4.5) */}
      <div>
        <div className="card__title">Timeline de eventos ({timeline.length})</div>
        <div style={{ position: "relative", paddingLeft: 14 }}>
          {timeline.length === 0 && (
            <div className="text-faint" style={{ fontSize: 11.5 }}>
              Sin eventos registrados todavía.
            </div>
          )}
          {timeline.map((t, idx) => (
            <div key={t.event_id ?? idx} style={{ position: "relative", paddingBottom: 12 }}>
              <span
                style={{
                  position: "absolute",
                  left: -14,
                  top: 3,
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: t.tipo.includes("consolidado")
                    ? "var(--c-danger)"
                    : t.tipo.includes("actualizado")
                      ? "var(--c-warn)"
                      : "var(--c-info)",
                }}
              />
              {idx < timeline.length - 1 && (
                <span
                  style={{
                    position: "absolute",
                    left: -10.5,
                    top: 11,
                    bottom: 0,
                    width: 1,
                    background: "var(--c-border)",
                  }}
                />
              )}
              <div style={{ fontSize: 12, color: "var(--c-text)" }}>{t.tipo}</div>
              <div className="text-faint" style={{ fontSize: 10.5 }}>
                {new Date(t.ts).toLocaleString("es-CL", {
                  day: "2-digit",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}{" "}
                · {t.actor}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cadena_Causal (R4.4) */}
      <div>
        <div className="card__title">Cadena causal ({cadena.length} vínculos)</div>
        {cadena.length === 0 && (
          <div className="text-faint" style={{ fontSize: 11.5 }}>
            Sin vínculos de causalidad.
          </div>
        )}
        {cadena.slice(0, 12).map((v, idx) => (
          <div key={idx} className="alert-component">
            <span>
              <span className="badge badge--neutral">{v.desde.tipo}</span>{" "}
              <span className="text-muted" style={{ fontSize: 11 }}>
                {v.desde.id}
              </span>
            </span>
            <strong className="hstack" style={{ gap: 4 }}>
              <Icon name="chevron-right" size={11} /> {v.hacia.tipo} {v.hacia.id}
            </strong>
          </div>
        ))}
      </div>

      {/* Entidades relacionadas (raíz + acceso al mapa) */}
      <div>
        <div className="card__title">Entidades relacionadas</div>
        <Link
          to={`/mapa?dma=${caso.entidad_raiz_ref.id}`}
          className="alert-component"
          style={{ textDecoration: "none", cursor: "pointer" }}
        >
          <span>
            <span className="badge badge--neutral">{caso.entidad_raiz_ref.tipo}</span>{" "}
            <span className="text-muted" style={{ fontSize: 11 }}>
              entidad raíz
            </span>
          </span>
          <strong className="hstack" style={{ gap: 4 }}>
            {caso.entidad_raiz_ref.id} <Icon name="chevron-right" size={11} />
          </strong>
        </Link>
      </div>

      <div className="hstack" style={{ flexWrap: "wrap" }}>
        <Link className="btn" to={`/mapa?dma=${caso.entidad_raiz_ref.id}`}>
          <Icon name="search" size={13} /> Ver en mapa
        </Link>
        <button
          className="btn btn--ghost"
          onClick={() => {
            exportTextPdf(
              `ficha-${caso.caso_id}.pdf`,
              `Ficha de caso ${caso.caso_id} — ${tituloDeCaso(caso, dma)}`,
              [
                `Score_Caso: ${score} (${banda})${caso.score.parcial ? " · parcial" : ""}`,
                `Entidad raiz: ${caso.entidad_raiz_ref.tipo} ${caso.entidad_raiz_ref.id}`,
                `Estado: ${caso.estado}`,
                `Evidencia correlacionada: ${caso.evidencia.length} pieza(s)`,
                "",
                "Explicabilidad (9 componentes):",
                ...contribuciones.map(
                  (c) =>
                    `  ${c.no_disponible ? "n/d" : (c.contribucion >= 0 ? "+" : "") + c.contribucion.toFixed(1)} ${c.etiqueta}`,
                ),
                "",
                "Acciones sugeridas:",
                ...acciones.map((a) => `  - ${a.accion_semantica_id}: ${a.razonamiento}`),
              ],
            );
            toast(`Ficha PDF de ${caso.caso_id} descargada`, "success");
          }}
        >
          <Icon name="report" size={13} /> Ficha PDF
        </button>
      </div>
    </aside>
  );
}
